/**
 * Experiência de busca por intenção → tabuleiro RPG.
 */
import './intent.css';
import { SearchHero } from './ui/SearchHero.js';
import { RpgBoard } from './ui/RpgBoard.js';
import { YouDied } from './ui/YouDied.js';
import { DungeonRitual } from './ui/DungeonRitual.js';
import { MapClothBackground } from './scene/MapClothBackground.js';
import {
  bootstrapVisitorSession,
  replayVisitorQuery,
  searchPortfolioIntent,
  updateVisitorConsent,
} from './lib/portfolioIntent.js';
import { setConsentHistory } from './lib/visitorSession.js';

const heroRoot = document.getElementById('intent-hero');
const boardRoot = document.getElementById('intent-board');
const stage = document.getElementById('intent-stage');
const atmosphere = document.querySelector('.intent-atmosphere');

if (!heroRoot || !boardRoot || !stage) {
  throw new Error('[intent] markup incompleto');
}

setConsentHistory(true);

const board = new RpgBoard(boardRoot);
const youDied = new YouDied(boardRoot);
const ritual = new DungeonRitual(document.body);
const mapCloth = atmosphere ? new MapClothBackground(atmosphere) : null;
let abortCtrl = null;

function clearResults() {
  board.hide();
  youDied.hide();
}

/**
 * Ritual cobre a latência da API (mínimo ~1.8s; hold até a resposta).
 * @template T
 * @param {(signal: AbortSignal) => Promise<T>} requestFn
 * @param {(value: T) => void | Promise<void>} onSuccess
 * @param {(message: string) => void} onError
 */
async function runWithRitual(requestFn, onSuccess, onError) {
  abortCtrl?.abort();
  ritual.dismiss();
  abortCtrl = new AbortController();
  const signal = abortCtrl.signal;

  stage.dataset.mode = 'board';
  hero.setBusy(true);
  clearResults();

  mapCloth?.setLoupeMode('board');
  mapCloth?.pause();
  ritual.begin();

  let outcome = null;
  try {
    const value = await requestFn(signal);
    outcome = { ok: true, value };
  } catch (err) {
    if (err?.name === 'AbortError') {
      if (abortCtrl?.signal === signal) {
        ritual.dismiss();
        mapCloth?.resume();
        hero.setBusy(false);
      }
      return;
    }
    outcome = { ok: false, err };
  }

  await ritual.complete();
  mapCloth?.resume();

  // Outra busca/reset assumiu o controller — não tocar no UI.
  if (signal.aborted || abortCtrl?.signal !== signal) {
    return;
  }

  try {
    if (outcome?.ok) {
      await onSuccess(outcome.value);
    } else {
      onError(outcome?.err?.message || 'Erro inesperado');
    }
  } finally {
    if (abortCtrl?.signal === signal) hero.setBusy(false);
  }
}

async function runSearch(query) {
  await runWithRitual(
    (signal) => searchPortfolioIntent({ query, signal }),
    async (result) => {
      if (result.outOfScope) {
        youDied.show({ query });
        return;
      }
      board.render(result.board, {
        isCached: result.isCached,
        source: result.source,
        similarity: result.similarity,
        query,
      });
      try {
        const bundle = await bootstrapVisitorSession({
          signal: abortCtrl?.signal,
        });
        hero.setMemory(bundle);
      } catch (err) {
        if (err?.name !== 'AbortError') {
          console.warn('[intent] memory refresh failed', err);
        }
      }
    },
    (message) => board.showError(message),
  );
}

async function runReplay(queryId) {
  await runWithRitual(
    (signal) => replayVisitorQuery(queryId, { signal }),
    (result) => {
      if (result.outOfScope) {
        youDied.show({ query: result.query });
        return;
      }
      board.render(result.board, {
        isCached: true,
        source: result.source,
        similarity: 1,
        query: result.query,
      });
    },
    (message) => board.showError(message || 'Erro ao reabrir histórico'),
  );
}

/** @type {{ focus: boolean, query: string }} */
const exploreUi = { focus: false, query: '' };

function pushExploreState(partial = {}) {
  if (partial.focus !== undefined) exploreUi.focus = Boolean(partial.focus);
  if (partial.query !== undefined) exploreUi.query = String(partial.query ?? '');
  const query = exploreUi.query;
  mapCloth?.setExploreState({
    focus: exploreUi.focus,
    typing: query.length > 0,
    queryLen: query.length,
  });
}

const hero = new SearchHero(heroRoot, {
  onSubmit: runSearch,
  onSuggest: runSearch,
  onReplay: runReplay,
  onFocusChange: (focused) => pushExploreState({ focus: focused }),
  onQueryChange: (query) => pushExploreState({ query }),
});

boardRoot.addEventListener('board:reset', () => {
  abortCtrl?.abort();
  ritual.dismiss();
  mapCloth?.setLoupeMode('search');
  mapCloth?.resume();
  clearResults();
  stage.dataset.mode = 'search';
  hero.setBusy(false);
  hero.focus();
});

window.setTimeout(() => hero.focus(), 80);

// Funil: registra visitante e carrega histórico (consentimento sempre ativo).
void (async () => {
  try {
    await updateVisitorConsent(true);
    const bundle = await bootstrapVisitorSession();
    hero.setMemory(bundle);
  } catch (err) {
    console.warn('[intent] bootstrap failed', err);
  }
})();
